import { Grid2, GridSize, TextField } from "@mui/material";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { LocationTreeSelector } from "../../../../shared/selectors";
import { partTypeCodes } from "@/pages/report/parts/components/TableParts";

type PartFieldKeys = "RFIDTag" | "PartName" | "LocationName";

type Props<T extends FieldValues> = {
	partTypeCode: "A" | "C" | "G" | "K" | "T";
	control: Control<T>;
	disabled?: boolean | { PartName?: boolean; Location?: boolean };
	hideTag?: boolean;
	locationSizeMd?: GridSize;
};

export const PartFields = <T extends Record<PartFieldKeys, any>>({
	partTypeCode,
	control,
	disabled,
	hideTag = false,
	locationSizeMd = 6,
}: Props<T>) => {
	const partTypeName = partTypeCodes.find((c) => c.Code == partTypeCode)?.Name;

	let disabledName: boolean;
	let disabledLocation: boolean;

	if (typeof disabled === "boolean") {
		// If disabled is a boolean, use it for both
		disabledName = disabled;
		disabledLocation = disabled;
	} else if (disabled && typeof disabled === "object") {
		// If disabled is an object, use its properties or false if not provided
		disabledName = disabled.PartName ?? false;
		disabledLocation = disabled.Location ?? false;
	} else {
		// If disabled is not provided or is invalid, default to false
		disabledName = false;
		disabledLocation = false;
	}

	return (
		<>
			<Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
				<Controller<T>
					name={"PartName" as Path<T>}
					control={control}
					rules={{ required: "Name is required" }}
					render={({ field, fieldState: { error } }) => (
						<TextField
							required
							label="Name"
							variant="outlined"
							{...field}
							disabled={disabledName}
							error={!!error}
							helperText={error?.message}
							slotProps={{
								htmlInput: {
									"data-test-id": `${partTypeName?.toLowerCase().replaceAll(" ", "_")}-name-input`,
								},
							}}
						/>
					)}
				/>
			</Grid2>
			{!hideTag && (
				<Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
					<Controller<T>
						name={"RFIDTag" as Path<T>}
						control={control}
						render={({ field }) => (
							<TextField
								label="Tag"
								variant="outlined"
								{...field}
								value={field.value ?? ""}
								disabled
							/>
						)}
					/>
				</Grid2>
			)}
			<Grid2 size={{ xs: 12, sm: 12, md: locationSizeMd }}>
				<Controller<T>
					name={"LocationId" as Path<T>}
					control={control}
					render={({ field }) => (
						<LocationTreeSelector
							label="Location"
							value={field.value}
							onChange={field.onChange}
							disabled={disabledLocation}
						/>
					)}
				/>
			</Grid2>
		</>
	);
};
